import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import { signedCount, signedPercent } from '../../lib/format.ts';
import { ArrowDownIcon, ArrowUpIcon } from '../icons.tsx';
import { SkeletonStatTile } from './Skeleton.tsx';

export interface Delta {
  /** Signed change. Positive always means the number went up. */
  value: number;
  /** `percent` renders 0.124 as +12.4%; `absolute` renders 3 as +3. */
  format?: 'percent' | 'absolute';
  /** The comparison window, e.g. "vs prior 30 days". */
  period?: string;
}

export interface StatTileProps {
  label: string;
  /** Pre-formatted through lib/format so every figure reads the same way. */
  value: ReactNode;
  /** Omit entirely when there is no prior period to compare against. */
  delta?: Delta | null;
  /**
   * Whether an increase is good. False for loss rate, ghost rate, days to
   * close, and anything else where up is bad.
   */
  higherIsBetter?: boolean;
  /** Small qualifier under the value, e.g. "38 leads". */
  hint?: string;
  loading?: boolean;
}

export function StatTile({
  label,
  value,
  delta,
  higherIsBetter = true,
  hint,
  loading = false,
}: StatTileProps) {
  if (loading) return <SkeletonStatTile />;

  return (
    <div className="surface-lift rounded-lg border border-border bg-surface p-4">
      <p className="text-xs font-medium text-fg-subtle">{label}</p>
      <p className="numeric mt-2 text-3xl font-semibold tracking-tighter text-fg">{value}</p>
      <div className="mt-2 flex items-center gap-2 text-xs">
        {delta ? <DeltaChip delta={delta} higherIsBetter={higherIsBetter} /> : null}
        {hint && <span className="text-fg-subtle">{hint}</span>}
      </div>
    </div>
  );
}

function DeltaChip({ delta, higherIsBetter }: { delta: Delta; higherIsBetter: boolean }) {
  const { value, format = 'percent', period } = delta;

  const flat = value === 0;
  const good = value > 0 === higherIsBetter;
  const tone = flat ? 'text-fg-subtle' : good ? 'text-won' : 'text-lost';

  const text = format === 'percent' ? signedPercent(value) : signedCount(value);
  const Arrow = value > 0 ? ArrowUpIcon : ArrowDownIcon;

  return (
    <span className="flex items-center gap-2">
      <span className={cx('numeric flex items-center gap-0.5 font-medium', tone)}>
        {!flat && <Arrow size={12} />}
        {text}
      </span>
      {period && <span className="text-fg-subtle">{period}</span>}
    </span>
  );
}
